/* eslint-disable react/style-prop-object --
   The DS ui/Button exposes a variant prop literally named `style`
   (style="stroke" | ...), which this rule mistakes for the DOM style object. */
import React, { useState } from 'react';
import { TableData } from '@/components';
import { Button } from '@/components/ui/button';
import { Column } from '@/types/common';
import { StockMovement, warehouseApi } from '@/services/api/warehouse';
import {
  codeCls,
  EmptyState,
  fmtDateTime,
  fmtQty,
  HubCell,
  LoadError,
  metaCls,
  nameCls,
  Section,
  StatusBadge,
  StatusMeta,
  useLoad,
} from './shared';

// The logistic web's "Riwayat pergerakan stok" under the stock table: the
// ledger, newest first, every hub (pinned to the client by the backend).
// No document column: GR/GI/picking codes lead to ops screens a client
// can't open.

/** Rows shown first, and added per "Muat lebih banyak". */
const MOVEMENT_PAGE = 20;
/** The ledger endpoint's newest-first cap. */
const MOVEMENT_MAX = 500;

const MOVEMENT_TYPE: Record<string, StatusMeta> = {
  INBOUND: { label: 'Inbound', tone: 'success' },
  OUTBOUND: { label: 'Outbound', tone: 'feature' },
  TRANSFER: { label: 'Transfer', tone: 'information' },
  ADJUSTMENT: { label: 'Penyesuaian', tone: 'warning' },
};

const COLUMNS: Column<StockMovement>[] = [
  {
    key: 'createdAt',
    title: 'Waktu',
    sticky: 'left',
    render: (_v, m) => (
      <span className="text-xs text-text-sub-600">
        {fmtDateTime(m.createdAt)}
      </span>
    ),
  },
  {
    key: 'type',
    title: 'Tipe',
    render: (_v, m) => <StatusBadge map={MOVEMENT_TYPE} value={m.type} />,
  },
  {
    key: 'sku',
    title: 'SKU / batch',
    render: (_v, m) => (
      <div className="max-w-[150px]">
        <p className={`${codeCls} truncate`} title={m.sku}>
          {m.sku}
        </p>
        <p className={`${metaCls} truncate font-mono`} title={m.batchNumber}>
          {m.batchNumber}
        </p>
      </div>
    ),
  },
  {
    key: 'hub',
    title: 'Hub Name',
    render: (_v, m) => <HubCell hub={m.hub} hubID={m.hubID} />,
  },
  {
    key: 'storage',
    title: 'Storage',
    render: (_v, m) => {
      const name = m.storageToName ?? m.storageFromName;
      return name ? (
        <span
          className={`${nameCls} block max-w-[150px] truncate`}
          title={name}
        >
          {name}
        </span>
      ) : (
        <span className={metaCls}>—</span>
      );
    },
  },
  {
    key: 'qtyDelta',
    title: 'Qty',
    align: 'right',
    render: (_v, m) => (
      <span
        className={`text-sm font-semibold tabular-nums ${
          m.qtyDelta > 0 ? 'text-success-dark' : 'text-text-strong-950'
        }`}
      >
        {m.qtyDelta > 0 ? '+' : ''}
        {fmtQty(m.qtyDelta)}
      </span>
    ),
  },
];

const StockMovementHistory: React.FC = () => {
  const [limit, setLimit] = useState(MOVEMENT_PAGE);
  const movements = useLoad(
    () => warehouseApi.listMovements({ limit }),
    [limit]
  );
  const rows = movements.data ?? [];
  const more = rows.length >= limit && limit < MOVEMENT_MAX;

  return (
    <Section title="Riwayat pergerakan stok" flush>
      {movements.error ? (
        <LoadError message={movements.error} onRetry={movements.reload} />
      ) : (
        <>
          <TableData<StockMovement>
            data={rows}
            columns={COLUMNS}
            loading={movements.loading && rows.length === 0}
            size="small"
            className="rounded-none border-0 shadow-none"
            emptyState={<EmptyState title="Belum ada pergerakan stok" />}
          />
          {more && (
            <div className="flex justify-center px-4 py-3">
              <Button
                tone="neutral"
                style="stroke"
                size="xs"
                loading={movements.loading}
                onClick={() =>
                  setLimit((l) => Math.min(MOVEMENT_MAX, l + MOVEMENT_PAGE))
                }
              >
                Muat lebih banyak
              </Button>
            </div>
          )}
        </>
      )}
    </Section>
  );
};

export default StockMovementHistory;
